import { dxtToLocalServerTime } from "@dexteel/mesf-core";
import { ArrowDownward, ArrowUpward } from "@mui/icons-material";
import {
  Box,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useMemo, useState } from "react";
import { Charge } from "@/pages/sy/models/Charge";
import { chargeSelectionStyles } from "@/pages/sy/styles/ChargeSelectionStyles";
import { HeatGroup } from "../ChargeSelectionScreen";
import { formatCraneMagnitude } from "../utils/formatCraneMagnitude";
import type { ScaledChargeTarget } from "../utils/heatWeightScaling";
import { StatusIndicator } from "./StatusIndicator";

type SortOrder = "asc" | "desc";

export const TableChargesSelection = ({
  selectedCharge,
  selectedHeatGroup,
  handleChargeSelect,
  scaledTargetsByChargeId,
  actualScrapWeight,
  actualTotalWeight,
  recipeTotalWeight,
  requestedHeatWeight,
}: {
  selectedCharge: Charge | null;
  selectedHeatGroup: HeatGroup | null;
  handleChargeSelect: (charge: Charge) => void;
  scaledTargetsByChargeId?: ReadonlyMap<number, ScaledChargeTarget>;
  actualScrapWeight: number;
  actualTotalWeight: number;
  recipeTotalWeight: number;
  requestedHeatWeight: number | null;
}) => {
  const theme = useTheme();
  const styles = chargeSelectionStyles(theme);
  const [sortOrder, setSortOrder] = useState<SortOrder>("asc");

  const sortedCharges = useMemo(() => {
    if (!selectedHeatGroup?.charges || !sortOrder) {
      return selectedHeatGroup?.charges || [];
    }

    return [...selectedHeatGroup.charges].sort((a, b) => {
      const chargeA = String(a.ChargeNumber || "");
      const chargeB = String(b.ChargeNumber || "");

      if (sortOrder === "asc") {
        return chargeA.localeCompare(chargeB, undefined, { numeric: true });
      } else {
        return chargeB.localeCompare(chargeA, undefined, { numeric: true });
      }
    });
  }, [selectedHeatGroup?.charges, sortOrder]);

  const handleSortToggle = () => {
    if (sortOrder === "desc") {
      setSortOrder("asc");
    } else if (sortOrder === "asc") {
      setSortOrder("desc");
    }
  };

  const renderTargetWeight = (charge: Charge) => {
    const scaledTarget =
      charge.ChargeId == null
        ? null
        : (scaledTargetsByChargeId?.get(charge.ChargeId) ?? null);

    if (scaledTarget) {
      return (
        <Box sx={styles.scaledTargetWeight}>
          <Box component="span" sx={styles.originalTargetWeight}>
            {formatCraneMagnitude(scaledTarget.originalTotalTargetWeight, "0")}
          </Box>
          <Box component="span" sx={styles.newTargetWeight}>
            {formatCraneMagnitude(scaledTarget.scaledTotalTargetWeight, "0")}
          </Box>
        </Box>
      );
    }

    return formatCraneMagnitude(charge.ManualTargetWeight);
  };

  const getScrapWeight = (charge: Charge) =>
    Number(charge.TotalWeight ?? 0) - Number(charge.FluxWeight ?? 0);

  return (
    <TableContainer sx={styles.chargesTableContainer}>
      <Table stickyHeader size="medium">
        <TableHead>
          <TableRow sx={styles.chargesTableHeader}>
            <TableCell width="14%" align="center">
              Recipe
            </TableCell>
            <TableCell width="9%" align="center">
              Bucket
            </TableCell>
            <TableCell width="12%" align="center">
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                Charge #
                <IconButton size="small" onClick={handleSortToggle}>
                  {sortOrder === "asc" && <ArrowUpward fontSize="small" />}
                  {sortOrder === "desc" && <ArrowDownward fontSize="small" />}
                </IconButton>
              </div>
            </TableCell>
            <TableCell width="13%" align="center">
              Scrap Weight
            </TableCell>
            <TableCell width="13%" align="center">
              Total Weight
            </TableCell>
            <TableCell width="14%" align="center">
              Target Weight
            </TableCell>
            <TableCell width="17%">Last Update</TableCell>
            <TableCell width="8%" align="center">
              Status
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {selectedHeatGroup ? (
            <>
              {sortedCharges.map((charge) => (
                <TableRow
                  key={charge.ChargeId}
                  sx={{
                    ...(styles.chargesTableRow as object),
                    ...(selectedCharge?.ChargeId === charge.ChargeId
                      ? (styles.chargesSelectedRow as object)
                      : {}),
                  }}
                  onClick={() => handleChargeSelect(charge)}
                >
                  <TableCell align="center">
                    {charge.RecipeName || "-"}
                  </TableCell>
                  <TableCell align="center">
                    {charge.BucketName || "-"}
                  </TableCell>
                  <TableCell align="center">
                    {charge.ChargeNumber || "-"}
                  </TableCell>
                  <TableCell align="center">
                    {formatCraneMagnitude(getScrapWeight(charge), "0")}
                  </TableCell>
                  <TableCell align="center">
                    {formatCraneMagnitude(charge.TotalWeight, "0")}
                  </TableCell>
                  <TableCell align="center">
                    {renderTargetWeight(charge)}
                  </TableCell>
                  <TableCell>
                    {charge.UpdateTimestamp
                      ? dxtToLocalServerTime(
                          charge.UpdateTimestamp,
                          "MM/dd HH:mm",
                        )
                      : "-"}
                  </TableCell>
                  <TableCell>
                    <Box sx={styles.statusCell}>
                      <Box sx={styles.statusIndicator}>
                        <StatusIndicator status={charge.ChargeStatusCode} />
                      </Box>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}

              <TableRow sx={styles.chargesTotalsRow}>
                <TableCell colSpan={3} align="right">
                  <Box component="span" sx={styles.magnitudeLabel}>
                    Totals
                  </Box>
                </TableCell>
                <TableCell align="center">
                  {formatCraneMagnitude(actualScrapWeight, "0")}
                </TableCell>
                <TableCell align="center">
                  {formatCraneMagnitude(actualTotalWeight, "0")}
                </TableCell>
                <TableCell align="center">
                  {requestedHeatWeight != null ? (
                    <Box sx={styles.scaledTargetWeight}>
                      <Box component="span" sx={styles.originalTargetWeight}>
                        {formatCraneMagnitude(recipeTotalWeight, "0")}
                      </Box>
                      <Box component="span" sx={styles.newTargetWeight}>
                        {formatCraneMagnitude(requestedHeatWeight, "0")}
                      </Box>
                    </Box>
                  ) : (
                    formatCraneMagnitude(recipeTotalWeight, "0")
                  )}
                </TableCell>
                <TableCell colSpan={2} />
              </TableRow>
            </>
          ) : (
            <TableRow>
              <TableCell colSpan={8} align="center">
                <Typography variant="h6" color="textSecondary">
                  Select a Heat to view charges
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
